/**
 * Analytics tab — derivation layer.
 *
 * Everything here reads the SAME payload the Workspace grid reads
 * (useForecastData → monthlyExpanded per account/month), so a number on this
 * tab can never disagree with the number a user sees in the grid. Nothing
 * fetches; nothing rounds early.
 *
 * The one non-obvious rule is sign, and there are two of them. Actuals and LY
 * actuals arrive from [Transaction] in accounting sign (revenue negative).
 * Budgets arrive with EXPENSES negative — a different convention again, and
 * the one that quietly turned a $12M expense budget into a 199.6% budget
 * margin before it was normalised. Forecast is the only basis already
 * display-signed. Both corrections reuse the grid's own helpers so this tab
 * and the grid can never drift apart.
 */

import type { ForecastAccount, MonthlyExpandedValues } from '../../types';
import { displayBudget } from '../workspace/columnRegistry';

export const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
] as const;

export const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export type MetricId = 'revenue' | 'expenses' | 'net';
export type ScopeId = 'all' | 'revenue' | 'expense';

const EPS = 0.005;

/** Transaction sign → P&L display sign. Mirrors PnLGrid.normalizeActualForDisplay. */
function normalizeActual(value: number, isExpense: boolean): number {
  return isExpense ? value : value * -1;
}

/** One side of the P&L for one month, in every basis the tab compares. */
export interface SideTotals {
  /** What the Workspace shows: actual for closed months (when roll-in is on), forecast after. */
  current: number;
  /** The version's own forecast — for a locked month this is the frozen snapshot. */
  forecast: number;
  budget: number;
  actual: number;
  lyActual: number;
  /** False when no account reported an LY figure, so the series gaps instead of plotting zero. */
  lySeen: boolean;
  /** False when nothing has posted yet, so an unposted closed month doesn't score as a miss. */
  actualSeen: boolean;
}

export interface MonthTotals {
  key: string;
  label: string;
  monthNumber: number;
  closed: boolean;
  locked: boolean;
  revenue: SideTotals;
  expense: SideTotals;
}

const emptySide = (): SideTotals => ({
  current: 0, forecast: 0, budget: 0, actual: 0, lyActual: 0, lySeen: false, actualSeen: false,
});

export interface BuildMonthlyParams {
  accounts: ForecastAccount[];
  closedMonthKeys: string[];
  lockedMonths: number[];
}

/** Fold every account into 12 month rows, one pass. */
export function buildMonthly({ accounts, closedMonthKeys, lockedMonths }: BuildMonthlyParams): MonthTotals[] {
  const closed = new Set(closedMonthKeys);
  const locked = new Set(lockedMonths);

  const months: MonthTotals[] = MONTH_KEYS.map((key, i) => ({
    key,
    label: MONTH_LABELS[i],
    monthNumber: i + 1,
    closed: closed.has(key),
    locked: locked.has(i + 1),
    revenue: emptySide(),
    expense: emptySide(),
  }));

  for (const account of accounts) {
    const isExpense = account.accountType !== 'Revenue';
    for (let i = 0; i < MONTH_KEYS.length; i += 1) {
      const key = MONTH_KEYS[i];
      const side = isExpense ? months[i].expense : months[i].revenue;
      const expanded: MonthlyExpandedValues | undefined = account.monthlyExpanded?.[key];

      side.current += account.monthlyValues?.[key] || 0;
      if (!expanded) continue;

      side.forecast += expanded.forecast || 0;
      side.budget += displayBudget(expanded.budget || 0, isExpense);

      const actual = normalizeActual(expanded.actual || 0, isExpense);
      const lyActual = normalizeActual(expanded.lyActual || 0, isExpense);
      side.actual += actual;
      side.lyActual += lyActual;
      if (Math.abs(expanded.actual || 0) > EPS) side.actualSeen = true;
      if (Math.abs(expanded.lyActual || 0) > EPS) side.lySeen = true;
    }
  }

  return months;
}

/** Pull one metric out of a month row on a given basis. */
export type Basis = 'current' | 'forecast' | 'budget' | 'actual' | 'lyActual';

export function metricValue(month: MonthTotals, metric: MetricId, basis: Basis): number {
  if (metric === 'revenue') return month.revenue[basis];
  if (metric === 'expenses') return month.expense[basis];
  return month.revenue[basis] - month.expense[basis];
}

/** True when the basis has anything to say for this month. */
export function metricSeen(month: MonthTotals, basis: Basis): boolean {
  if (basis === 'lyActual') return month.revenue.lySeen || month.expense.lySeen;
  if (basis === 'actual') return month.revenue.actualSeen || month.expense.actualSeen;
  return true;
}

export function marginPct(month: MonthTotals, basis: Basis): number | null {
  const revenue = month.revenue[basis];
  if (!Number.isFinite(revenue) || Math.abs(revenue) < EPS) return null;
  return ((revenue - month.expense[basis]) / revenue) * 100;
}

// ---------------------------------------------------------------- completeness

export interface CompletenessBucket {
  name: string;
  done: number;
  total: number;
}

export interface CompletenessResult {
  done: number;
  total: number;
  pct: number | null;
  /** Months a value is required in for an account to count as complete. */
  scopeMonths: string[];
  scopeLabel: string;
  byDepartment: CompletenessBucket[];
}

export interface CompletenessParams {
  accounts: ForecastAccount[];
  closedMonthKeys: string[];
  /** A budget covers all twelve; a forecast is only answerable for the months still open. */
  isBudgetVersion: boolean;
  scope: ScopeId;
}

/**
 * An account counts toward the denominator when it has history to forecast
 * FROM (any LY actual). It counts as complete when every in-scope month
 * carries a value — a driver-produced amount, or an explicit override, which
 * is how an account deliberately set to zero reads as answered rather than
 * forgotten.
 */
export function buildCompleteness({
  accounts,
  closedMonthKeys,
  isBudgetVersion,
  scope,
}: CompletenessParams): CompletenessResult {
  const closed = new Set(closedMonthKeys);
  const scopeMonths = isBudgetVersion
    ? [...MONTH_KEYS]
    : MONTH_KEYS.filter((m) => !closed.has(m));

  const openLabels = scopeMonths.map((m) => MONTH_LABELS[MONTH_KEYS.indexOf(m)]);
  const scopeLabel = isBudgetVersion
    ? 'all 12 months'
    : openLabels.length === 0
      ? 'no open months'
      : openLabels.length === 1
        ? openLabels[0]
        : `${openLabels[0]}–${openLabels[openLabels.length - 1]}`;

  const departments = new Map<string, CompletenessBucket>();
  let done = 0;
  let total = 0;

  for (const account of accounts) {
    const isRevenue = account.accountType === 'Revenue';
    if (scope === 'revenue' && !isRevenue) continue;
    if (scope === 'expense' && isRevenue) continue;

    const hasHistory = MONTH_KEYS.some(
      (m) => Math.abs(account.monthlyExpanded?.[m]?.lyActual || 0) > EPS
    );
    if (!hasHistory) continue;

    const complete = scopeMonths.every((m) => {
      if (account.monthlyOverrides?.[m] === true) return true;
      return Math.abs(account.monthlyExpanded?.[m]?.forecast || 0) > EPS;
    });

    total += 1;
    if (complete) done += 1;

    const name = account.department?.trim() || 'Unassigned';
    const bucket = departments.get(name) || { name, done: 0, total: 0 };
    bucket.total += 1;
    if (complete) bucket.done += 1;
    departments.set(name, bucket);
  }

  return {
    done,
    total,
    pct: total > 0 ? (done / total) * 100 : null,
    scopeMonths,
    scopeLabel,
    // Least complete first: this list is a worklist, not a leaderboard.
    byDepartment: Array.from(departments.values()).sort(
      (a, b) => a.done / a.total - b.done / b.total || b.total - a.total
    ),
  };
}

// -------------------------------------------------------------------- accuracy

export interface AccuracyRow {
  key: string;
  label: string;
  actual: number;
  forecast: number;
  variance: number;
  variancePct: number | null;
  favourable: boolean;
  locked: boolean;
}

export interface AccuracyResult {
  rows: AccuracyRow[];
  /** Closed months that exist at all — the denominator for "N of M scorable". */
  closedCount: number;
  /** Mean absolute percentage error across scored months. */
  mape: number | null;
  /** Signed mean — which way the version leans. */
  bias: number | null;
  withinTolerance: number;
  tolerance: number;
  largest: AccuracyRow | null;
  lockedCount: number;
}

/**
 * Closed AND LOCKED months only: what actually posted against what this
 * version said would post.
 *
 * The lock is not a nicety, it is the whole basis of the measurement. Locking
 * freezes the month's forecast, and that frozen value is the only honest thing
 * to score actuals against. A closed month that was never locked still has a
 * "forecast" — but it is a live recalculation whose drivers now read the very
 * actuals being scored, so comparing the two measures nothing. Scoring those
 * months produced a 675% mean error on real data before this guard existed.
 *
 * Sign is read against the P&L, not the number line: an expense line landing
 * UNDER forecast is favourable, a revenue line landing under forecast is not.
 */
export function buildAccuracy(
  months: MonthTotals[],
  metric: MetricId,
  tolerance = 2
): AccuracyResult {
  const rows: AccuracyRow[] = [];
  const closedCount = months.filter((m) => m.closed).length;

  for (const month of months) {
    if (!month.closed || !month.locked) continue;
    // Nothing posted yet — a closed month with no ledger is a data gap, not a miss.
    if (!month.revenue.actualSeen && !month.expense.actualSeen) continue;

    const actual = metricValue(month, metric, 'actual');
    const forecast = metricValue(month, metric, 'forecast');
    const variance = actual - forecast;
    rows.push({
      key: month.key,
      label: month.label,
      actual,
      forecast,
      variance,
      variancePct: Math.abs(forecast) > EPS ? (variance / Math.abs(forecast)) * 100 : null,
      favourable: metric === 'expenses' ? variance <= 0 : variance >= 0,
      locked: month.locked,
    });
  }

  const scored = rows.filter((r) => r.variancePct !== null);
  const pcts = scored.map((r) => r.variancePct as number);
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

  let largest: AccuracyRow | null = null;
  for (const row of rows) {
    if (!largest || Math.abs(row.variance) > Math.abs(largest.variance)) largest = row;
  }

  return {
    rows,
    closedCount,
    mape: mean(pcts.map(Math.abs)),
    bias: mean(pcts),
    withinTolerance: pcts.filter((p) => Math.abs(p) <= tolerance).length,
    tolerance,
    largest,
    lockedCount: rows.filter((r) => r.locked).length,
  };
}
